import { memo, useMemo } from 'react'
import { getSecurityKey } from '../../../../util/security'
import LiveMarketText from '../../../../components/common/LiveMarketText'
import {
    getAskDisplaySelector,
    getBidDisplaySelector,
    getFieldDisplaySelector,
    getIvDisplaySelector,
    getLtpChangeColorSelector,
    getLtpChangeDisplaySelector,
    getLtpDisplaySelector,
    getOiChangeColorSelector,
    getOiChangeDisplaySelector,
    getOiDisplaySelector,
} from './optionChainLiveDisplay'

export const greekColumns = ['delta', 'gamma', 'vega', 'theta', 'rho']

export const formatLtpChangePercent = (num: string) => {
    const numb = Math.abs(parseFloat(num))
    if (numb < 10) {
        return numb?.toFixed(2).padStart(4, '0') + '%'
    }
    if (numb < 100) {
        return numb?.toFixed(1).padStart(4, '0') + '%'
    }
    return numb?.toFixed(1) + '%'
}

export const formatGreeks = (num: number) => {
    if (num === 0) return '-'
    if (Math.abs(num) >= 100) return num?.toFixed(0) ?? '-'
    if (Math.abs(num) >= 10) return num?.toFixed(1) ?? '-'
    if (Math.abs(num) >= 0) return num?.toFixed(2) ?? '-'
}

const LIVE_TEXT_CLASS = 'relative z-10 font-feature-num'

const DynamicColumnText = ({
    obj,
    columnId,
    subColumn,
}: {
    obj: any
    columnId: string
    subColumn?: string
    maxOiChange?: number
}) => {
    const secKey = getSecurityKey(obj?.security?.exchange ?? 0, obj?.security?.code ?? '')
    const fullSecurity = useMemo(() => obj.security, [obj])
    const priceMultiplier = fullSecurity?.priceMultiplier ?? 100
    const lotSize = fullSecurity?.lotSize ?? 50

    let opacity = 0.8

    if (columnId === 'openInterest') {
        return (
            <div
                className="option-chain-oi-value z-10"
                style={{
                    fontWeight: 500,
                    pointerEvents: 'none',
                }}
            >
                <LiveMarketText
                    className={LIVE_TEXT_CLASS}
                    selectText={getOiDisplaySelector(secKey, Number(obj?.openInterest ?? 0), lotSize)}
                />
            </div>
        )
    }

    if (columnId === 'oiChange|outlook') {
        return (
            <div
                className="option-chain-oi-value z-10"
                style={{
                    fontWeight: 500,
                    pointerEvents: 'none',
                }}
            >
                <LiveMarketText
                    className={LIVE_TEXT_CLASS}
                    selectText={getOiChangeDisplaySelector(secKey, Number(obj?.oiChanges?.oiChange?.change ?? 0), lotSize)}
                    selectColor={getOiChangeColorSelector(secKey, Number(obj?.oiChanges?.oiChange?.change ?? 0))}
                />
            </div>
        )
    }

    if (columnId === 'ltp') {
        return (
            <LiveMarketText
                className={LIVE_TEXT_CLASS}
                selectText={getLtpDisplaySelector(secKey, Number(obj?.ltp ?? 0), priceMultiplier)}
            />
        )
    }

    if (columnId === 'ltpChange') {
        return (
            <LiveMarketText
                className={LIVE_TEXT_CLASS}
                selectText={getLtpChangeDisplaySelector(secKey, Number(obj?.ltpChange ?? 0), priceMultiplier)}
                selectColor={getLtpChangeColorSelector(secKey, Number(obj?.ltpChange ?? 0), priceMultiplier)}
            />
        )
    }

    if (columnId === 'impliedVolatility') {
        return (
            <div className={LIVE_TEXT_CLASS} style={{ opacity: opacity + 0.2, pointerEvents: 'none' }}>
                <LiveMarketText
                    selectText={getIvDisplaySelector(secKey, Number(obj?.impliedVolatility ?? 0))}
                />
            </div>
        )
    }

    if (columnId === 'bid') {
        return (
            <LiveMarketText
                className={`${LIVE_TEXT_CLASS} text-right`}
                selectText={getBidDisplaySelector(secKey, Number(obj?.topBid ?? 0), priceMultiplier)}
            />
        )
    }

    if (columnId === 'ask') {
        return (
            <LiveMarketText
                className={`${LIVE_TEXT_CLASS} text-right`}
                selectText={getAskDisplaySelector(secKey, Number(obj?.topAsk ?? 0), priceMultiplier)}
            />
        )
    }

    const formatLarge = ['openInterest', 'volume'].includes(columnId)

    return (
        <div
            className="relative z-10"
            style={{
                opacity: opacity + 0.2,
                pointerEvents: 'none',
            }}
        >
            <LiveMarketText
                className={LIVE_TEXT_CLASS}
                selectText={getFieldDisplaySelector(
                    secKey,
                    columnId,
                    greekColumns.includes(columnId)
                        ? (obj?.optionGreeks?.[columnId] ?? obj?.[columnId] ?? '-')
                        : (obj?.[columnId] ?? '-'),
                    {
                        lotSize,
                        formatLarge,
                    }
                )}
            />
        </div>
    )
}

export default memo(DynamicColumnText)
